import React, { useEffect, useState } from 'react';
import { Box, ButtonBase, Typography } from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import { hubForPath, visibleHubs, defaultPath, NavPerms, FALLBACK_MODULES } from '../../config/navHubs';
import { useAuth } from '../../contexts/AuthContext';
import { useBranding } from '../../contexts/ThemeContext';
import apiClient from '../../services/api/config';
import {
  Home, School, Spa, Assessment, EmojiEvents, Assignment, Forum, BeachAccess,
} from '@mui/icons-material';

const MANAGER_ROLES = ['manager', 'lead', 'admin', 'company-super-admin', 'super-super-admin'];
const ADMIN_ROLES = ['admin', 'company-super-admin', 'super-super-admin'];

// Hub id → bottom-bar icon (mirrors NavigationRail's HUB_ICONS).
const HUB_ICONS: Record<string, React.ReactNode> = {
  home: <Home />, grow: <School />, wellbeing: <Spa />, performance: <Assessment />,
  rewards: <EmojiEvents />, workspace: <Assignment />, connect: <Forum />, leave: <BeachAccess />,
};

/**
 * Persistent mobile bottom bar — every enabled hub as an always-visible icon.
 * Tapping a hub lands on its first module; the per-hub module tabs live in HubTabs.
 * Replaces the hamburger drawer as the primary navigation on small screens.
 */
export default function BottomHubBar() {
  const { user } = useAuth();
  const { branding, mode } = useBranding();
  const navigate = useNavigate();
  const location = useLocation();
  const [enabledModules, setEnabledModules] = useState<string[]>(FALLBACK_MODULES);

  useEffect(() => {
    let active = true;
    apiClient.get('/company/modules')
      .then((res) => {
        if (!active) return;
        const mods = (res.data?.data || []).filter((m: { isActive: boolean }) => m.isActive).map((m: { moduleName: string }) => m.moduleName);
        if (mods.length) setEnabledModules(mods);
      })
      .catch(() => {});
    return () => { active = false; };
  }, []);

  const isManager = !!user && MANAGER_ROLES.includes(user.role);
  const isAdmin = !!user && ADMIN_ROLES.includes(user.role);
  const navPerms: NavPerms = { enabledModules, isManager, isAdmin };

  const hubs = visibleHubs(navPerms);
  const activeHub = hubForPath(location.pathname);

  const barBg = mode === 'dark' ? (branding.sidebarColor || '#1A1D1F') : '#FFFFFF';
  const primary = branding.primaryColor || '#2D5BE3';
  const idle = mode === 'dark' ? 'rgba(255,255,255,0.55)' : 'rgba(0,0,0,0.5)';

  return (
    <Box
      component="nav"
      sx={{
        display: { xs: 'flex', md: 'none' },
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        zIndex: 1100,
        backgroundColor: barBg,
        borderTop: `1px solid ${mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)'}`,
        boxShadow: '0 -2px 12px rgba(0,0,0,0.12)',
        paddingBottom: 'env(safe-area-inset-bottom)',
        paddingLeft: 'env(safe-area-inset-left)',
        paddingRight: 'env(safe-area-inset-right)',
        alignItems: 'stretch',
      }}
    >
      {hubs.map((hub) => {
        const target = defaultPath(hub, navPerms);
        const active = activeHub?.id === hub.id;
        return (
          <ButtonBase
            key={hub.id}
            onClick={() => target && navigate(target)}
            aria-label={hub.label}
            aria-current={active ? 'page' : undefined}
            sx={{
              flex: '1 1 0',
              minWidth: 0,
              height: 58,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '3px',
              position: 'relative',
              color: active ? primary : idle,
              transition: 'color 0.15s ease',
              '& svg': { fontSize: 23 },
              '&::before': active ? {
                content: '""',
                position: 'absolute',
                top: 0,
                width: 26,
                height: 3,
                borderRadius: '0 0 3px 3px',
                backgroundColor: primary,
              } : {},
            }}
          >
            {HUB_ICONS[hub.icon]}
            <Typography
              noWrap
              sx={{
                fontSize: '9.5px',
                lineHeight: 1,
                fontWeight: active ? 700 : 500,
                letterSpacing: '0.1px',
                maxWidth: '100%',
                px: '2px',
              }}
            >
              {hub.label}
            </Typography>
          </ButtonBase>
        );
      })}
    </Box>
  );
}
